<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Bulk Edit Notifications</span>
    </div>

    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>

    <transition name="slide-up">
      <div v-if="!isLoading" class="p-20-px flex-grow-1 overflow-y-auto">
        <!-- Notification Selection Table -->
        <a-row>
          <a-col :span="24">
            <div class="section-box">
              <div class="section-header-row">
                <div class="ant-form-item-label">
                  <label>Select Notifications</label>
                </div>
                <a-tag v-if="selectedIds.length > 0" color="blue" class="selected-tag">
                  {{ selectedIds.length }} selected
                </a-tag>
              </div>
              <a-row :gutter="16" class="mt-3">
                <a-col :span="8">
                  <div class="a-input-group" :class="{ active: !!searchTerm }">
                    <a-button size="large">
                      <a-icon type="search" />
                    </a-button>
                    <a-input class="custom-prefix" placeholder="Search by URL" @change="onSearch" />
                  </div>
                </a-col>
                <a-col :span="8">
                  <a-select
                    v-model="filterContactGroupId"
                    allowClear
                    showSearch
                    :filterOption="filterOption"
                    placeholder="Filter by Contact Group"
                    size="large"
                    class="full-w"
                    @change="filter"
                  >
                    <a-select-option v-for="group in contactGroups" :key="group.id" :value="group.id">
                      {{ group.description }}
                    </a-select-option>
                  </a-select>
                </a-col>
                <a-col :span="8">
                  <a-select
                    v-model="filterNotificationType"
                    allowClear
                    placeholder="Filter by Notification Type"
                    size="large"
                    class="full-w"
                    @change="filter"
                  >
                    <a-select-option v-for="type in notificationTypes" :key="type.id" :value="type.id">
                      {{ type.name }}
                    </a-select-option>
                  </a-select>
                </a-col>
              </a-row>
              <div class="table-wrapper mt-2">
                <PaginationTable
                  :pagination="pagin"
                  :columns="BulkNotificationSelectColumn"
                  :dataSource="filterData"
                  :tableSize="tableSize"
                  :hasSelectRow="true"
                  @changeSelectedRow="onSelectionChange"
                >
                  <div slot="url" slot-scope="{ record }" class="text-truncate d-block">
                    <a :href="record.url && record.url.url" class="url">
                      {{ record.url && record.url.url }}
                    </a>
                  </div>
                  <div slot="contact_group" slot-scope="{ record }">
                    {{ record.contact_group && record.contact_group.description }}
                  </div>
                  <div slot="notification_type_name" slot-scope="{ record }">
                    {{ record.notification_type_name }}
                  </div>
                </PaginationTable>
              </div>
              <div class="pagination-wrapper mt-3">
                <PaginationBar
                  :pageSize.sync="pagin.pageSize"
                  :total="pagin.total"
                  @change="paginChange"
                />
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- Bulk Edit Fields -->
        <a-row :gutter="24" class="mt-4">
          <a-col :span="12">
            <a-form-item>
              <template slot="label">
                New Contact Group
                <a-tooltip placement="top">
                  <template slot="title">
                    <span>Leave empty to keep the current contact group for each notification.</span>
                  </template>
                  <a-icon type="info-circle" theme="filled" class="ml-1" />
                </a-tooltip>
              </template>
              <a-select
                v-model="newContactGroupId"
                allowClear
                showSearch
                :filterOption="filterOption"
                placeholder="Keep current"
                size="large"
              >
                <a-select-option v-for="group in contactGroups" :key="group.id" :value="group.id">
                  {{ group.description }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item>
              <template slot="label">
                New Notification Type
                <a-tooltip placement="top">
                  <template slot="title">
                    <span>Leave empty to keep the current notification type for each notification.</span>
                  </template>
                  <a-icon type="info-circle" theme="filled" class="ml-1" />
                </a-tooltip>
              </template>
              <a-select
                v-model="newNotificationType"
                allowClear
                placeholder="Keep current"
                size="large"
              >
                <a-select-option v-for="type in notificationTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- Progress Section -->
        <a-row class="mt-4" v-if="isSaving">
          <a-col :span="24">
            <div class="section-box">
              <div class="ant-form-item-label">
                <label>Update Progress</label>
              </div>
              <a-progress
                :percent="progressPercent"
                :status="updateProgress.failed > 0 ? 'exception' : 'active'"
              />
              <p class="progress-text mt-2 mb-0">
                Updating {{ updateProgress.completed }} of {{ updateProgress.total }} notifications...
                <span v-if="updateProgress.failed > 0" class="text-danger">
                  ({{ updateProgress.failed }} failed)
                </span>
              </p>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>

    <!-- Footer -->
    <div class="a-footer">
      <a-button key="back" size="large" class="mr-3 slide-down-enter-active" @click="onCancel">
        Cancel
      </a-button>
      <a-button
        key="submit"
        size="large"
        type="primary"
        class="slide-down-enter-active"
        :disabled="!canSave"
        :loading="isSaving"
        @click="onSave"
      >
        Save Changes
      </a-button>
    </div>
  </div>
</template>

<script>
  import { NOTIFICATION_TYPES, NOTIFICATION_TYPE_SMS } from '@constants/notifications';
  import bulkEditMixin from '@shared/mixins/bulkEdit.mixin';
  import { BulkNotificationSelectColumn } from '../columnsTable/BulkNotificationSelectColumn';
  import NotificationService from '../services/notification.service';
  import ContactGroupService from '@modules/ContactGroup/services/contact-group.service';

  export default {
    name: 'BulkNotificationEdit',
    mixins: [bulkEditMixin],
    data() {
      return {
        BulkNotificationSelectColumn,
        notifications: [],
        contactGroups: [],
        notificationTypes: [],
        filterContactGroupId: undefined,
        filterNotificationType: undefined,
        newContactGroupId: undefined,
        newNotificationType: undefined
      };
    },
    computed: {
      featureAccess() {
        return this.$store.state.user.data.feature_access;
      },
      isShowSMSNotification() {
        return Boolean(this.featureAccess['sms']);
      },
      canSave() {
        return (
          this.selectedIds.length > 0 &&
          (this.newContactGroupId != null || this.newNotificationType != null) &&
          !this.isSaving
        );
      }
    },
    mounted() {
      this.getNotificationTypes();
      Promise.all([this.getNotificationList(), this.getContactGroupList()]).then(() => {
        this.isLoading = false;
      });
    },
    methods: {
      async getNotificationList() {
        try {
          const data = await NotificationService.getList();
          this.notifications = (data || []).map((item, index) => {
            const notifyType = NOTIFICATION_TYPES.find((type) => type.id == item.notification_type);
            return {
              ...item,
              index: index + 1,
              notification_type_name: notifyType && notifyType.name,
              isChecked: false
            };
          });
          this.filter();
        } catch (error) {
          this.$showMultiErrorMessage(error.messages || []);
        }
      },
      async getContactGroupList() {
        try {
          this.contactGroups = await ContactGroupService.getList();
        } catch {
          this.$showErrorMessage('Failed to load contact groups');
        }
      },
      getNotificationTypes() {
        if (!this.isShowSMSNotification) {
          this.notificationTypes = NOTIFICATION_TYPES.filter((item) => item.id !== NOTIFICATION_TYPE_SMS);
        } else {
          this.notificationTypes = NOTIFICATION_TYPES;
        }
      },
      filter() {
        const term = this.searchTerm.trim().toLowerCase();
        this.filterData = this.notifications.filter((item) => {
          if (term && !(item.url && item.url.url && item.url.url.toLowerCase().includes(term))) {
            return false;
          }
          if (this.filterContactGroupId != null && item.contact_group_id != this.filterContactGroupId) {
            return false;
          }
          if (this.filterNotificationType != null && item.notification_type != this.filterNotificationType) {
            return false;
          }
          return true;
        });
        this.pagin = {
          current: 1,
          pageSize: this.pagin.pageSize,
          total: this.filterData.length
        };
        this.pruneSelection(this.notifications);
      },
      async onSave() {
        if (!this.canSave) return;

        const confirmed = await this.showBulkSaveConfirm(this.selectedIds.length, 'notification');
        if (!confirmed) return;

        const results = await this.executeBatchUpdates(this.selectedIds, async (id) => {
          const notification = this.notifications.find((n) => n.id === id);
          if (!notification) throw new Error(`Notification ${id} not found in local data`);
          const payload = {
            url_id: notification.url_id,
            contact_group_id: this.newContactGroupId != null ? this.newContactGroupId : notification.contact_group_id,
            notification_type: this.newNotificationType != null ? this.newNotificationType : notification.notification_type
          };
          await NotificationService.update(id, payload);
        });

        if (results.failed.length === 0) {
          this.$showSuccessMessage(`Successfully updated ${results.success.length} notifications`);
          this.$router.push('/notification/list');
        } else if (results.success.length > 0) {
          this.$showWarningMessage(
            `Updated ${results.success.length} notifications, but ${results.failed.length} failed`
          );
          await this.getNotificationList();
        } else {
          this.$showErrorMessage('Failed to update all notifications');
        }
      },
      onCancel() {
        this.$router.push('/notification/list');
      }
    }
  };
</script>

<style lang="scss" scoped>
  .section-box {
    background-color: #f7f7f7;
    padding: 20px;
    border-radius: 4px;

    .ant-form-item-label {
      label::after {
        content: '';
      }
    }
  }

  .section-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .selected-tag {
    font-size: 14px;
  }

  .full-w {
    width: 100%;
  }

  .table-wrapper {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    background: #fff;
  }

  .pagination-wrapper {
    background: #fff;
    padding: 12px 16px;
    border: 1px solid #e8e8e8;
    border-top: none;
    border-radius: 0 0 4px 4px;
  }

  .progress-text {
    color: #666;
  }

  .text-danger {
    color: #ff4d4f;
  }
</style>
